import { FormikHelpers } from "formik";
import { isNil } from "lodash-es";
import React from "react";
import { withTranslation, WithTranslation } from "react-i18next";

import { getCancelTokenSource, IHttpCancelTokenSource } from "@/browser";
import { IEditableSectionProps } from "@/components/EditableSection";
import { applyApiErrors } from "@/components/Formik";
import { finalMapPhone } from "@/data/phones";
import { ISessionUser, refresh } from "@/data/session";
import { putUser } from "@/data/users";
import { IWithMessagesActions, withMessagesActions } from "@/eworkitComponents";

import { IUserFormikProps } from "./types";

interface IUserEditComponentProps {
  source: ISessionUser;
  onUpdate?: (user: ISessionUser) => void;
  onCancel: () => void;
  editableProps?: IEditableSectionProps;
}

interface IConfig {
  validationSchema: any;
  messangerContext: string;
}

export function userPartialUpdaterBuilder<T, P extends IUserFormikProps<T>>(
  Component: React.ComponentType<P>,
  config: IConfig,
) {
  interface Props
    extends IWithMessagesActions,
      IUserEditComponentProps,
      WithTranslation,
      Pick<IUserFormikProps<T>, "initialValues"> {}

  class UserGeneralComponent extends React.PureComponent<Props> {
    source: IHttpCancelTokenSource | null;

    componentWillUnmount() {
      if (!isNil(this.source)) {
        this.source.cancel();
      }
    }

    handleSubmit = async (values: T, actions: FormikHelpers<T>) => {
      const { source, onUpdate, onCancel, t } = this.props;

      try {
        const data = {
          ...source,
          ...values,
        };

        this.source = getCancelTokenSource();
        await putUser(
          { userId: source.id },
          {
            ...data,
            phone: finalMapPhone(data.phone),
          },
          { cancelToken: this.source.token },
        );
        const sessionUser = await refresh();

        if (onUpdate) {
          onUpdate(sessionUser);
        }

        onCancel();
      } catch (response) {
        this.props.addMessage({
          text: t("VALIDATION_MESSAGES.GENERAL"),
          type: "warning",
          context: config.messangerContext,
        });
        applyApiErrors(actions, response.payload);
      }

      actions.setSubmitting(false);
    };

    render() {
      const { initialValues, t, ...rest } = this.props;

      return (
        <Component
          validationSchema={config.validationSchema}
          initialValues={initialValues}
          onSubmit={this.handleSubmit}
          {...(rest as any)}
        />
      );
    }
  }

  return withMessagesActions(withTranslation("common")(UserGeneralComponent));
}
